/**
 * DocumentMoveModal Component
 * 
 * Modal for moving documents between folders
 */

import React, { useState } from 'react';
import { Document, DocumentFolder } from '../types/documentsTypes';
import { X, FolderInput, ChevronRight, Folder, Home, Check } from 'lucide-react';
import { cn } from '@/core/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface DocumentMoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMove: (targetFolderId: string | null) => void;
  documents: Document[];
  folders: DocumentFolder[];
  currentFolderId: string | null;
  isLoading?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

interface FolderNode extends DocumentFolder {
  children: FolderNode[];
  level: number;
}

const buildFolderTree = (folders: DocumentFolder[]): FolderNode[] => {
  const rootFolders: FolderNode[] = [];
  const folderMap = new Map<string, FolderNode>();

  // Create nodes
  folders.forEach(folder => {
    folderMap.set(folder.id, { ...folder, children: [], level: 0 });
  });

  // Build tree
  folders.forEach(folder => {
    const node = folderMap.get(folder.id)!;
    if (folder.parent_id) {
      const parent = folderMap.get(folder.parent_id);
      if (parent) {
        node.level = parent.level + 1;
        parent.children.push(node);
      } else {
        rootFolders.push(node);
      }
    } else {
      rootFolders.push(node);
    }
  });

  return rootFolders;
};

// ============================================================================
// Subcomponents
// ============================================================================

interface FolderSelectItemProps {
  folder: FolderNode;
  selectedId: string | null;
  currentFolderId: string | null;
  disabledIds: string[];
  onSelect: (folderId: string) => void;
  level: number;
}

const FolderSelectItem: React.FC<FolderSelectItemProps> = ({
  folder,
  selectedId,
  currentFolderId,
  disabledIds,
  onSelect,
  level,
}) => {
  const [expanded, setExpanded] = useState(true);
  const isSelected = selectedId === folder.id;
  const isCurrent = currentFolderId === folder.id;
  const isDisabled = disabledIds.includes(folder.id);
  const hasChildren = folder.children.length > 0;

  return (
    <div>
      <button
        onClick={() => !isDisabled && onSelect(folder.id)}
        disabled={isDisabled}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-left',
          isSelected && 'bg-sage/10 text-sage',
          !isSelected && !isDisabled && 'hover:bg-cream',
          isDisabled && 'opacity-50 cursor-not-allowed',
          isCurrent && 'border border-dashed border-warm-gray'
        )}
        style={{ paddingLeft: `${12 + level * 20}px` }}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            <ChevronRight className={cn(
              'w-4 h-4 text-warm-gray transition-transform',
              expanded && 'rotate-90'
            )} />
          </button>
        )}
        {!hasChildren && <div className="w-5" />}
        
        <Folder className={cn(
          'w-4 h-4 flex-shrink-0',
          isSelected ? 'text-sage' : 'text-warm-gray'
        )} />
        
        <span className="truncate flex-1">{folder.name}</span>
        
        {isCurrent && (
          <span className="text-xs text-warm-gray">(current)</span>
        )}
        
        {isSelected && (
          <Check className="w-4 h-4 text-sage" />
        )}
      </button>

      {expanded && hasChildren && (
        <div>
          {folder.children.map(child => (
            <FolderSelectItem
              key={child.id}
              folder={child}
              selectedId={selectedId}
              currentFolderId={currentFolderId}
              disabledIds={disabledIds}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const DocumentMoveModal: React.FC<DocumentMoveModalProps> = ({
  isOpen,
  onClose,
  onMove,
  documents,
  folders,
  currentFolderId,
  isLoading = false,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // Build folder tree
  const folderTree = buildFolderTree(folders);

  // Get IDs of folders that contain selected documents (can't move to current location)
  const disabledIds: string[] = [];
  if (currentFolderId) {
    disabledIds.push(currentFolderId);
  }

  const handleMove = () => {
    onMove(selectedFolderId);
  };

  const documentCount = documents.length;
  const documentLabel = documentCount === 1 ? 'document' : 'documents';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-navy/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
              <FolderInput className="w-5 h-5 text-sage" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-navy">
                Move {documentCount} {documentLabel}
              </h2>
              <p className="text-sm text-warm-gray">
                Select destination folder
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-cream transition-colors"
          >
            <X className="w-5 h-5 text-warm-gray" />
          </button>
        </div>

        {/* Folder List */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Root option */}
          <button
            onClick={() => setSelectedFolderId(null)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-left mb-2',
              selectedFolderId === null && 'bg-sage/10 text-sage',
              selectedFolderId !== null && 'hover:bg-cream',
              currentFolderId === null && 'border border-dashed border-warm-gray'
            )}
          >
            <div className="w-5" />
            <Home className={cn(
              'w-4 h-4',
              selectedFolderId === null ? 'text-sage' : 'text-warm-gray'
            )} />
            <span className="flex-1">All Documents (Root)</span>
            {currentFolderId === null && (
              <span className="text-xs text-warm-gray">(current)</span>
            )}
            {selectedFolderId === null && (
              <Check className="w-4 h-4 text-sage" />
            )}
          </button>

          {/* Folder tree */}
          {folderTree.map(folder => (
            <FolderSelectItem
              key={folder.id}
              folder={folder}
              selectedId={selectedFolderId}
              currentFolderId={currentFolderId}
              disabledIds={disabledIds}
              onSelect={setSelectedFolderId}
              level={0}
            />
          ))}

          {folders.length === 0 && (
            <div className="text-center py-8 text-warm-gray">
              <Folder className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No folders yet</p>
              <p className="text-sm">Create folders to organize your documents</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-warm-gray hover:text-navy transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={isLoading}
            className={cn(
              'px-6 py-2 rounded-lg font-medium transition-all',
              'bg-sage text-white hover:bg-sage-dark',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isLoading ? 'Moving...' : 'Move Here'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentMoveModal;

